import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Form, Input, Select } from 'antd'
import type { CustomerUpsertInput } from '@super-mega/server/contracts/customer'
import { CustomerBasicFields } from '@/features/master-data/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import type { CustomerOption, SalespersonOption } from './service-order-form.types.ts'

type MemberType = 'employee' | 'agent'

export function QuickCustomerModal({
  onClose,
  onCreated
}: {
  onClose: () => void
  onCreated: (customer: CustomerOption) => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const create = useMutation(trpc.customer.upsert.mutationOptions())
  const salespeople = useQuery(trpc.lookup.members.queryOptions({ purpose: 'customer.read' }))
  return (
    <ModalForm<Pick<CustomerUpsertInput, 'name' | 'type' | 'companyInfo' | 'salespersonId' | 'tagIds'>>
      title="快速创建客户"
      width="48rem"
      open={open}
      initialValues={{ type: 'normal', tagIds: [] }}
      disabled={create.isPending}
      modalProps={{
        destroyOnHidden: true,
        afterClose: onClose,
        mask: { closable: false },
        closable: !create.isPending,
        keyboard: !create.isPending
      }}
      onOpenChange={(nextOpen) => {
        if (!create.isPending) {
          setOpen(nextOpen)
        }
      }}
      submitter={{ searchConfig: { submitText: '创建并选择' }, resetButtonProps: { disabled: create.isPending } }}
      onFinish={async (values) => {
        try {
          const customer = await create.mutateAsync({
            data: { ...values, name: values.name.trim(), salespersonId: values.salespersonId ?? null }
          })
          onCreated({
            value: customer.id,
            label: customer.name,
            salesperson: customer.salesperson
              ? {
                  value: customer.salesperson.id,
                  label: customer.salesperson.name,
                  isDisable: customer.salesperson.isDisable
                }
              : null
          })
          setOpen(false)
          void message.success('客户已创建并选中')
          void cache.invalidateQueries(trpc.customer.options.queryFilter())
          void cache.invalidateQueries(trpc.customer.list.queryFilter())
          return true
        } catch (error) {
          void message.error(getErrorMessage(error, '创建客户失败'))
          return false
        }
      }}
    >
      <CustomerBasicFields salespersonOptions={salespeople.data ?? []} salespeopleLoading={salespeople.isFetching} />
    </ModalForm>
  )
}

export function QuickSalespersonModal({
  types,
  onClose,
  onCreated
}: {
  types: MemberType[]
  onClose: () => void
  onCreated: (salesperson: SalespersonOption) => void
}) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const create = useMutation(trpc.member.upsert.mutationOptions())
  return (
    <ModalForm<{ type: MemberType; name: string }>
      title="快速创建业务员"
      width="28rem"
      open={open}
      initialValues={{ type: types[0] }}
      disabled={create.isPending}
      modalProps={{
        destroyOnHidden: true,
        afterClose: onClose,
        mask: { closable: false },
        closable: !create.isPending,
        keyboard: !create.isPending
      }}
      onOpenChange={(nextOpen) => {
        if (!create.isPending) {
          setOpen(nextOpen)
        }
      }}
      submitter={{ searchConfig: { submitText: '创建并选择' }, resetButtonProps: { disabled: create.isPending } }}
      onFinish={async (values) => {
        try {
          const member = await create.mutateAsync({
            data: {
              type: values.type,
              name: values.name.trim(),
              jobTitle: values.type === 'employee' ? '业务员' : null,
              email: null,
              isSalesperson: true
            }
          })
          onCreated({ value: member.id, label: member.name, isDisable: false })
          setOpen(false)
          void message.success('业务员已创建并选中')
          void cache.invalidateQueries(trpc.customer.salespersonOptions.queryFilter())
          void cache.invalidateQueries(trpc.member.list.queryFilter())
          return true
        } catch (error) {
          void message.error(getErrorMessage(error, '创建业务员失败'))
          return false
        }
      }}
    >
      <Form.Item label="成员类型" name="type" rules={[{ required: true }]}>
        <Select options={types.map((value) => ({ value, label: value === 'employee' ? '内部员工' : '代理商' }))} />
      </Form.Item>
      <Form.Item label="姓名" name="name" rules={[{ required: true, whitespace: true, min: 2, max: 80 }]}>
        <Input maxLength={80} />
      </Form.Item>
    </ModalForm>
  )
}
